// Сверка месяца: выдали, потратили, осталось. Одна цепочка, крупно ровно одна величина.
//
// ## Какой вопрос закрывает
//
// Гендиректор заказчика 29.09.2026: суточные расходы больше не ведутся в Excel, «я хочу видеть
// их так же прозрачно и наглядно»; уточнение финдиректора: «что-то ему дали и куда он их
// потратил». В книге внизу листа «итог» стоит «Остаток»: получено в подотчёт 13 311 851,
// потрачено 13 309 020, осталось 2 831. Здесь то же самое, теми же словами.
//
// ## Считает сервер, экран ничего не вычитает
//
// Правило 5 ПРАВИЛ: деньги считаются только на сервере. «Осталось» приходит готовым числом
// (`left_total`), и экран его не пересчитывает: клиент, вычитающий сам, однажды разойдётся с
// файлом Excel, который собирает сервер.
//
// ## Ряд читается как формула
//
// «Выдано − Потрачено = Осталось», а когда были возвраты в кассу, между ними встаёт
// «− Вернули в кассу» (только не ноль: нулевое слагаемое читается как «вычли ноль» и ничего не
// объясняет). Знак действия стоит в подписи, а не отдельной колонкой (`ui-views.md`).
//
// Меньше нуля — подпись «= Потрачено сверх выданного», значение БЕЗ знака: знак уже сказан
// словами. Тот же приём, что в сводке ведомостей («+ Доплаты» при отрицательной разнице).
//
// ## Возвраты при отборе по юрлицу
//
// У возврата нет юрлица, поэтому при выбранном юрлице сервер их не считает, и под рядом сказано
// об этом прямо. Ряд без пометки читался бы как «возвратов не было».

import { RiScalesLine } from '@remixicon/react';

import type { CashFlowReportOut } from '@/shared/api/endpoints/reports';
import { formatRubles } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { Block } from '@/shared/ui/block';
import { Metric, MetricRow } from '@/shared/ui/metric';

/** «48 выдач», «1 выдача». */
const ВЫДАЧИ = ['выдача', 'выдачи', 'выдач'] as const;
/** «690 принятых заявок», «1 принятая заявка». */
const ПРИНЯТЫЕ_ЗАЯВКИ = ['принятая заявка', 'принятые заявки', 'принятых заявок'] as const;

/**
 * Есть ли у отчёта сверка месяца. Условие ОДНО на блок и на экран, который под ним стоит:
 * от него зависит, повторять ли итог «Потрачено» крупным числом ниже (`СтруктураСуммы`).
 * Разойдись два условия, и итог исчез бы с экрана совсем либо встал бы дважды.
 */
export function естьСверка(данные: CashFlowReportOut): boolean {
  return (
    данные.issued_total !== undefined &&
    данные.issued_total !== null &&
    данные.left_total !== undefined &&
    данные.left_total !== null
  );
}

export interface СверкаМесяцаProps {
  данные: CashFlowReportOut;
  /** Месяц словами, со строчной: «май 2026». Берётся из самих данных, а не из адреса. */
  месяц: string;
  /** Выбрано ли юрлицо: только тогда отсутствие возвратов объясняется словами. */
  юрлицоВыбрано: boolean;
  /** Подпись среза данных: «данные на 14:32». */
  срез?: string;
}

export function СверкаМесяца({ данные, месяц, юрлицоВыбрано, срез }: СверкаМесяцаProps) {
  // Сервер не строит сверку тем, кому касса не выдаёт (старший объекта, охранник), при отборе по
  // человеку и старый сервер её не знает вовсе. Во всех случаях блока нет: «выдано 0, потрачено
  // сверх выданного» про человека, у которого деньги приходят передачей, было бы неправдой.
  if (!естьСверка(данные)) {
    return null;
  }
  const выдано = данные.issued_total ?? 0;
  const осталось = данные.left_total ?? 0;

  // Пусто (возвраты не считались) и ноль одинаково не дают слагаемого в ряду.
  const вернули = данные.returned_total ?? 0;
  const сверх = осталось < 0;
  const возвратыНеУчтены = данные.returned_total === null && юрлицоВыбрано;

  return (
    <Block icon={RiScalesLine} title='Сверка месяца' note={срез}>
      <MetricRow columns={вернули > 0 ? 4 : 3}>
        <Metric
          label={`Выдано за ${месяц}`}
          value={formatRubles(выдано)}
          note={countLabel(данные.issued_count ?? 0, ВЫДАЧИ)}
        />
        <Metric
          label='− Потрачено'
          value={formatRubles(данные.total_amount)}
          note={countLabel(данные.total_claims, ПРИНЯТЫЕ_ЗАЯВКИ)}
        />
        {вернули > 0 && (
          <Metric label='− Вернули в кассу' value={formatRubles(вернули)} note='в этом месяце' />
        )}
        <Metric
          tone='важный'
          label={сверх ? '= Потрачено сверх выданного' : '= Осталось'}
          // Без знака: «сверх» уже сказано подписью, а «−30 000» рядом с ним читалось бы как
          // второе отрицание.
          value={formatRubles(Math.abs(осталось))}
          note={
            сверх
              ? 'принятый расход больше выдач за этот месяц'
              : 'от выданного в этом месяце; остаток на руках на вкладке «Подотчёт»'
          }
        />
      </MetricRow>
      {возвратыНеУчтены && (
        <p className='mt-3 text-paragraph-xs text-text-sub-600'>
          Возвраты по юрлицам не разделены: в сверке не учтены
        </p>
      )}
    </Block>
  );
}
